import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { Country } from '../country.js';

export class AdAccount extends DataModel {
  static readonly TYPE_ID: string = '150c0922126a45b4a6dcea36e3def4e4';

  private constructor(
    public readonly uniqueId: string,
    public readonly organizationId: string | null,
    public readonly name: string,
    public readonly country: Country,
    public readonly metaApiAccessTokenId: string,
    public readonly adAccountId: string,
    public readonly pixelId: string,
    public readonly beneficiary: string,
    public readonly payor: string,
  ) {
    super();
  }

  get $typeId(): string {
    return AdAccount.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    organizationId: string | null,
    name: string,
    country: Country,
    metaApiAccessTokenId: string,
    adAccountId: string,
    pixelId: string,
    beneficiary: string,
    payor: string,
  }): AdAccount {
    return new AdAccount(
        args.uniqueId,
        args.organizationId,
        args.name,
        args.country,
        args.metaApiAccessTokenId,
        args.adAccountId,
        args.pixelId,
        args.beneficiary,
        args.payor
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): AdAccount {
    return AdAccount.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      organizationId: MapUtils.getStringOrNull(map, 'organizationId'),
      name: MapUtils.getString(map, 'name'),
      country: toEnumFromString(MapUtils.get<string>(map, 'country'), Country),
      metaApiAccessTokenId: MapUtils.getString(map, 'metaApiAccessTokenId'),
      adAccountId: MapUtils.getString(map, 'adAccountId'),
      pixelId: MapUtils.getString(map, 'pixelId'),
      beneficiary: MapUtils.getString(map, 'beneficiary'),
      payor: MapUtils.getString(map, 'payor'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'organizationId': this.organizationId,
      'name': this.name,
      'country': fromEnumAsString<Country>(this.country),
      'metaApiAccessTokenId': this.metaApiAccessTokenId,
      'adAccountId': this.adAccountId,
      'pixelId': this.pixelId,
      'beneficiary': this.beneficiary,
      'payor': this.payor,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    organizationId = this.organizationId,
    name = this.name,
    country = this.country,
    metaApiAccessTokenId = this.metaApiAccessTokenId,
    adAccountId = this.adAccountId,
    pixelId = this.pixelId,
    beneficiary = this.beneficiary,
    payor = this.payor,
  }: {
    uniqueId?: string,
    organizationId?: string | null,
    name?: string,
    country?: Country,
    metaApiAccessTokenId?: string,
    adAccountId?: string,
    pixelId?: string,
    beneficiary?: string,
    payor?: string
  } = {}): this {
    return AdAccount.create({
      uniqueId,
      organizationId,
      name,
      country,
      metaApiAccessTokenId,
      adAccountId,
      pixelId,
      beneficiary,
      payor,
    }) as this;
  }

  isSame(other: AdAccount): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.organizationId === this.organizationId &&
      other.name === this.name &&
      other.country === this.country &&
      other.metaApiAccessTokenId === this.metaApiAccessTokenId &&
      other.adAccountId === this.adAccountId &&
      other.pixelId === this.pixelId &&
      other.beneficiary === this.beneficiary &&
      other.payor === this.payor;
  }
}

export class AdAccountBuilder extends DataModelBuilder<AdAccount> {
  build(context: SerializationContext, map: SerializedData): AdAccount {
    return AdAccount.fromMap(context, map);
  }
}
